// @ts-check
"use strict"

/*
 * This file is the VPS model, and it is loaded by every page that so much as
 * lists a cloud server -- the dashboard, AgencyHub, the service pickers.
 * Those pages never invoke the management actions below; they only render
 * names and states. Because the URL scan works per file rather than per call
 * site, all of it counted towards every one of those pages, so a sub-user
 * without manageVPS could not open the dashboard at all.
 *
 * The actions are therefore tagged @optional: they are triggered by a user
 * gesture on a VPS management page, never during page load. The pages that do
 * offer them declare @permission manageVPS for themselves, so the requirement
 * lands on the pages that actually need it instead of on everything that
 * happens to load this file.
 *
 * @optional /a/vps/{n}/backups
 * @optional /a/vps/{n}/rootPassword
 * @optional /a/vps/{n}/vnc
 * @optional /a/vps/{n}/changePassword
 * @optional /a/vps/{n}/lockVnc
 * @optional /a/vps/{n}/rebuildOperatingSystems
 * @optional /a/vps/{n}/restoreBackup
 * @optional /a/vps/{n}/reverseDNS
 * @optional /a/vps/{n}/unlockVnc
 */

/**
 * @typedef VPSIn
 * @property {string} id
 * @property {boolean} [enabled]
 */

/**
 * @typedef VPSIPAddressIn
 * @property {string} ip
 * @property {string} subnetMask
 * @property {string} defaultGateway
 */

/**
 * @typedef VPSApplicationIn
 * @property {number} ApplicationId
 * @property {string} DisplayName
 * @property {string} Version
 */

/**
 * @typedef VPSIPAddressInfo
 * @property {string} ip
 * @property {string} subnetMask
 * @property {string} defaultGateway
 * @property {?string} hostname
 */

/**
 * An installable app
 */
class VPSApplication {
    /**
     * Builds the object
     * @param {VPSApplicationIn} result
     */
    constructor(result) {
        this.name = result.DisplayName
        this.version = result.Version
        this.id = result.ApplicationId
    }
}

/**
 * @typedef VPSOperatingSystemSpecIn
 * @property {number} VpsOsId
 * @property {string} LibvirtOsType
 * @property {string} LibvirtOsVariant
 * @property {string} DisplayName
 */

 /**
  * @typedef {GET.Services.vps.id.output & {oid: string}} vpsInfo
  */

/**
 * An OS
 */
class VPSOperatingSystemSpec {
    /**
     * Builds the object
     * @param {VPSOperatingSystemSpecIn} result
     */
    constructor(result) {
        this.id = result.VpsOsId
        this.osType = result.LibvirtOsType
        this.osVariant = result.LibvirtOsVariant
        this.name = result.DisplayName
    }
    /** True if it doesn't have a custom app install */
    get isStock() {
        return !this.name.match(/cPanel|Stack/)
    }
}

/**
 * @typedef VPSReverseDNS
 * @property {string} ref
 * @property {string} target
 */

/**
 * An IP address for a VPS
 */
class VPSIPAddress extends NetworkPropertySet {
    /**
     * Builds the object
     * @param {VPS} vps
     * @param {VPSIPAddressIn} address
     * @param {?VPSReverseDNS} reverse_dns
     */
    constructor(vps, address, reverse_dns, version) {
        super()
        this.vps = vps
        this.ip = address.ip
        this._hostname = null
        this.subnetMask = address.subnetMask
        this.defaultGateway  = address.defaultGateway
        this.reverseDNS = reverse_dns
        this.version = address.version || 4;
    }
    get networkPropertyHandlers() {
        return {
            reverseDNS: () => $.ajax(`/a/vps/${this.vps.id}/reverseDNS`).then(
                /** @param {GET.Services.vps.id.reverseDNS.output} result */
                result => $.Deferred().resolve(result[this.ip])
            ),
        }
    }
    get hostname() {
        return this.reverseDNS && this.reverseDNS.target
    }
    set hostname(v) {
        let a = $.ajax({
            method: "post",
            url: `/a/vps/${this.vps.id}/reverseDNS`,
            data: {
                [this.ip]: {
                    new: {
                        target: v,
                    },
                    delete: this.reverseDNS ? [this.reverseDNS.ref] : [],
                },
            }
        })
    }

    /**
     * Returns a promise which will be resolved or rejected when hostname is
     * written.
     * @param {*} v
     * @returns {JQuery.Promise}
     */
    promiseHostname(v) {
        return $.ajax({
            method: "post",
            url: `/a/vps/${this.vps.id}/reverseDNS`,
            data: {
                [this.ip]: {
                    new: {
                        target: v,
                    },
                    delete: this.reverseDNS ? [this.reverseDNS.ref] : [],
                },
            }
        })
    }
}

/**
 * @typedef VNCIn
 * @property {{IpAddress: string}[]} VNCIPAddressACL
 * @property {string|null} VncPassword
 * @property {string|null} VNCHostname
 * @property {number|null} PublicVncPort
 * @property {string|null} VNCDisplay
 * @property {boolean} Enabled
 * @property {{VNCDisplay?: string|null}} [Status]
 */

/**
 * VNC info
 */
class VNC {
    /**
     * Builds the object
     * @param {string} vps_id
     * @param {VNCIn} vnc
     */
    constructor(vps_id, vnc) {
        this.id = vps_id
        this.setDetails(vnc)
    }

    setDetails(vnc) {
        this.unlockIp = Array.isArray(vnc.VNCIPAddressACL) ? vnc.VNCIPAddressACL : [];
        this.password = vnc.VncPassword || null;
        this.hostname = vnc.VNCHostname || null;
        this.port = vnc.PublicVncPort || null;
        this.display = vnc.VNCDisplay ?? vnc.Status?.VNCDisplay ?? null;
        this.enabled = !!vnc.Enabled;
    }

    get connectionAddress() {
        if (this.hostname && this.port) return this.hostname + ':' + this.port;
        return this.display;
    }

    refresh() {
        return $.ajax({url: `/a/vps/${this.id}/vnc`, cache: false}).then(details => {
            this.setDetails(details);
            return this;
        });
    }

    /** @type {boolean} */
    get locked() {
        return !this.enabled || !this.unlockIp.length
    }

    get unlockedFor() {
        return this.unlockIp.map(
            i => i.IpAddress
        )
    }

    /**
     * @returns {JQuery.Promise}
     */
    lock() {
        return $.ajax({
            url: `/a/vps/${this.id}/lockVnc`,
            method: "post",
            data: {
                lock: true,
            }
        }).then(() => this.refresh()).done(() => {
            ShowNotification.success(
                translation.get('vps_service_overview_shared_notification_success_locked_vnc')
            );
        }).fail(() => {
            ShowNotification.fail(
                translation.get('vps_service_overview_shared_notification_fail_locking_vnc')
            );
        });
    }
    /**
     * Unlocks for the given items
     * @param {string} ips
     * @returns {JQuery.Promise}
     */
    unlockFor(ips) {
        return $.ajax({
            method: "post",
            url: `/a/vps/${this.id}/unlockVnc`,
            data: {
                IpAddress: ips,
            }
        }).then(() => this.refresh()).done(() => {
            ShowNotification.success(
                translation.get('vps_service_overview_shared_notification_success_unloc_vnc')
            );
        }).fail(() => {
            ShowNotification.fail(
                translation.get('vps_service_overview_shared_notification_fail_unlocking_vnc')
            );
        })
    }
}

/**
 * Helps the display of statuses
 */
class VPSStatusDisplay {
    /**
     * Builds the object
     * @param {VPS} vps
     */
    constructor(vps) {
        this.vps = vps
    }
    /**
     * @type {?string} eg. "active"
     */
    get htmlClass() {
        if(!this.vps.status) return null
        switch(this.vps.status) {
            default:
                return this.vps.status.toLowerCase()
        }
    }
    /**
     * @type {?string} eg. "Active"
     */
    get label() {
        if(!this.vps.status) return null
        if (this.vps.status === 'active' && this.vps.info && this.vps.info.cpanelRebuilding === true) return translation.get('vps_service_overview_shared_installing_cpanel')
        switch(this.vps.status) {
            case "active": return translation.get('vps_service_overview_shared_active')
            case "build": return translation.get('vps_service_overview_shared_building')
            case "offline": return translation.get('vps_service_overview_shared_offline')
            case "restarting": return translation.get('vps_service_overview_shared_restarting')
            case "disabled": return translation.get('vps_service_overview_shared_disabled')
            case "unknown": return translation.get('vps_service_overview_shared_not_available')
            case "wait":
                switch(this.vps.currentPlatformAction) {
                    case "build": return translation.get('vps_service_overview_shared_building')
                    case "destroy": return translation.get('vps_service_overview_shared_n_a')
                    case "hard_reboot": return translation.get('vps_service_overview_shared_restarting')
                    case "os_disk_resize": return translation.get('vps_service_overview_shared_upgrading')
                    case "patient_shutdown_cannot_skip": return "Pending Shutdown"
                    case "patient_shutdown_can_skip": return translation.get('vps_service_overview_shared_n_a')
                    case "reboot": return translation.get('vps_service_overview_shared_restarting')
                    case "rebuild": return translation.get('vps_service_overview_shared_rebuilding')
                    case "reconcile_cpu_cores": return translation.get('vps_service_overview_shared_upgrading')
                    case "reconcile_ram_mb": return translation.get('vps_service_overview_shared_upgrading')
                    case "reset_linux_root_password": return translation.get('vps_service_overview_shared_resetting_password')
                    case "restore_snapshot": return translation.get('vps_service_overview_shared_restoring_snapshot')
                    case "shutdown": return translation.get('vps_service_overview_shared_shutting_down')
                    case "start": return translation.get('vps_service_overview_shared_starting_up')
                    case "undefine": return translation.get('vps_service_overview_shared_n_a')
                    case "unprovision": return translation.get('vps_service_overview_shared_n_a')
                    case "update_nwfilters": return translation.get('vps_service_overview_shared_n_a')
                    case "update_vnc_ip_acls": return translation.get('vps_service_overview_shared_n_a')
                    default:
                        return translation.get('vps_service_overview_shared_busy')
                }
            default: return this.vps.status
        }
    }
}

class VPSPlatform extends NetworkPropertySet {
    static get inst() {
        if(!this._inst) {
            this._inst = new VPSPlatform()
        }
        return this._inst
    }
    constructor() {
        super()
        /** @type {?VPSApplication[]} */
        this.apps = null
        /** @type {?VPSOperatingSystemSpec[]} */
        this.operatingSystems = null
    }

    get networkPropertyHandlers() {
        return {
            apps: () => $.ajax(`/a/platform/vps/Application`).then(
                /** @param {GET.Services.platform.vps.Application.output} result */
                result => $.Deferred().resolve(
                    result.result.filter(r => r.Available).map(
                        r => new VPSApplication(r)
                    ).sort(
                        (a, b) => a.name.localeCompare(b.name)
                    )
                )
            ),
            operatingSystems: async () => {
                /** @type {GET.Services.platform.vps.VPSOS.output} */
                const result = await $.ajax(`/a/platform/vps/VPSOS`)
                return result.result.map(
                    r => new VPSOperatingSystemSpec(r)
                )
            },
        }
    }
}

/**
 * A copy of the VPS content.
 */
class VPSBackupCopy {
    /**
     * Builds the object
     * @param {VPS} vps
     * @param {VPSBackup} vps_backup
     * @param {Date} from_date When the backup was taken
     */
    constructor(vps, vps_backup, from_date) {
        this.fromDate = from_date
        this.vps = vps
        this.vpsBackup = vps_backup
    }

    /** @type {string} */
    get name() {
        return translation.sprintf(
            translation.get('vps_service_overview_shared_backup_from_date'),
            [tdUtils.createDate(this.fromDate), tdUtils.createTime(this.fromDate)]
        )
    }

    /** @type {?boolean} */
    get successfulBackup() {
        return this.vpsBackup.takenSuccessfully;
    }

    /** @type {?boolean} */
    get restoreInProgress() {
        return this.vpsBackup.restoreInProgress;
    }

    /**
     * Restores the copy
     * @returns {JQuery.Promise}
     */
    restore() {
        if(this.vps.info && this.vps.info.Status) {
            this.vps.info.Status.CurrentAction = "rebuild"
        }
        return $.ajax({
            method: "post",
            url: `/a/vps/${this.vps.id}/restoreBackup`,
            data: {
                "Action": "restore_snapshot",
                "Input": this.vpsBackup.id,
                "State": "new"
            }
        })
    }
}

/**
 * A VPS backup service
 */
class VPSBackup {
    /**
     * Builds the object
     * @param {VPS} vps
     * @param {Object} backup_service_in
     */
    constructor(vps, backup_service_in) {
        this.vps = vps
        this.id = backup_service_in.SnapshotSlotId
        this._timing = backup_service_in.Schedule
        this.backupTimeGuess = backup_service_in.LastTakenDate
        this.takenSuccessfully = backup_service_in.TakenSuccessfully
        this.restoreInProgress = backup_service_in.pendingRestore
    }

    /**
     * @type {VPSBackupCopy[]}
     */
    get copies() {
        return [
            new VPSBackupCopy(this.vps, this, moment(this.backupTimeGuess).toDate())
        ]
    }

    /** @type {string} */
    get timing() {
        return this._timing
    }
    set timing(v) {
        if(v !== this._timing) {
            $.ajax({
                method: "post",
                url: `/a/vps/${this.vps.id}/backups`,
                data: {
                    [this.id]: {
                        Schedule: v
                    }
                }
            }).done(
                () => {
                    ShowNotification.success(
                        translation.get('vps_service_overview_shared_notification_success_updated_backup_interval')
                    )
                    this._timing = v
                }
            )
        }
    }
}

/**
 * A suggested operating system
 */
class VPSSuggestedOperatingSystem {
    /**
     * Builds the object
     * @param {string} code
     * @param {VPSOperatingSystemSpec} result
     */
    constructor(code, result) {
        this.code = code
        this.name = result ? result.name : ""
    }

    /**
     * Returns true if a valid rebuild option
     * @param {VPSSuggestedOperatingSystem} other_option
     * @returns {boolean}
     */
    mayRebuildFrom(other_option) {
        return true
    }
}


/**
 * A group of suggested operating systems
 */
class VPSSuggestedOperatingSystemGroup {
    /**
     * Returns new groups for the given options
     * @param {VPSSuggestedOperatingSystem[]} os_options
     * @returns {VPSSuggestedOperatingSystemGroup[]}
     */
    static from(os_options) {
        var grouped = {};
        os_options.forEach(
            os => {
                let name = os.name.replace(/ .*/, '')
                if(!grouped[name]) {
                    grouped[name] = new VPSSuggestedOperatingSystemGroup(name)
                }
                grouped[name].operatingSystems.push(os)
            }
        )
        return Object.values(grouped)
    }
    /**
     * Builds the object
     * @param {string} name
     */
    constructor(name) {
        this.name = name
        /**
         * @type {VPSSuggestedOperatingSystem[]}
         */
        this.operatingSystems = []
        this.selectedVersion = null
    }
    get versions() {
        return this.operatingSystems.map(
            os => os.name
        )
    }
    get options() {
        return this.operatingSystems.map(
            os => ({id: os.code, text: os.name})
        )
    }
    sortOperatingSystems() {
        this.operatingSystems.sort(
            (a, b) => b.name.localeCompare(a.name)
        )
    }
}

class VPSPlatformInfo {
    /**
     * @param {vpsInfo} info
     */
    constructor(info) {
        this.info = info
    }

    /** @type {?number} eg. 2 for 2 cores */
    get cpuCores() {
        return this.info.configuration?.CpuCores || null
    }

    /** @type {?number} eg. 21474836480 for 20GB */
    get diskSize() {
        return this.info?.configuration?.OsDiskSizeGb * (1024 * 1024 * 1024) || null
    }

    /** @type {?number} eg. 2147483648 for 2GB */
    get memorySize() {
        return this.info.configuration?.RamMb && this.info.configuration.RamMb * 1024 * 1024 || null
    }

    /** @type {?string} eg. "rhel6" */
    get operatingSystemCode() {
        return(
            this.info &&
            this.info.OS &&
            this.info.OS.LibvirtOsVariant ||
            null
        )
    }

    /** @type {?string} eg. "CentOS 6.6 (x86_64, rebuild, console disabled)" */
    get operatingSystemLabel() {
        return this.info && this.info.OS && this.info.OS.DisplayName || null
    }
}

class VPSConfiguredInfo {
    /**
     * @param {vpsInfo["configuration"]} configuration
     * @param {vpsInfo["productOptions"]} product_options
     */
    constructor(configuration, product_options) {
        this.configuration = configuration
        this.productOptions = product_options
    }

    /** @type {?number} eg. 2 for 2 cores */
    get cpuCores() {
        return this.configuration && this.configuration.CpuCores || null
    }

    /** @type {?number} eg. 21474836480 for 20GB */
    get diskSize() {
        return this.configuration && (
            this.configuration.DataDiskSizeGb * (1024 * 1024 * 1024) +
            this.configuration.OsDiskSizeGb * (1024 * 1024 * 1024)
        ) || null
    }

    /** @type {?number} eg. 2147483648 for 2GB */
    get memorySize() {
        return this.configuration && this.configuration.RamMb * 1024 * 1024 || null
    }

    get operatingSystem() {
        if(!VPSPlatform.inst.operatingSystems) return null
        if(this.configuration && this.configuration.VpsOsId) {
            const os_id = this.configuration.VpsOsId
            return VPSPlatform.inst.operatingSystems.find(a => a.id == os_id)
        } else {
            return(
                this.productOptions.os &&
                VPSPlatform.inst.operatingSystems.find(
                    a => a.osVariant == this.productOptions.os
                )
            )
        }
    }

    /** @type {?string} eg. "rhel6" */
    get operatingSystemCode() {
        return(
            this.operatingSystem &&
            this.operatingSystem.osVariant ||
            null
        )
    }

    /** @type {?string} eg. "CentOS 6.6 (x86_64, rebuild, console disabled)" */
    get operatingSystemLabel() {
        return(
            this.operatingSystem &&
            this.operatingSystem.name ||
            null
        )
    }
}

/**
 * A VPS
 */
class VPS extends NetworkPropertySet {
    /**
     * Builds the object
     * @param {VPSIn & {oid?: string}} item
     * @param {?Store} store
     */
    constructor(item, store = null) {
        super()
        /** @type {?VPSBackup[]} */
        this.backups = null
        this.count = item.count || 0;
        this.disks = null;
        this.enabled = item.enabled
        this.id = item.id
        this.isCloud = !!item.isCloud;
        this.isManaged = !!item.isManaged;
        this.isResellerCloudBranded = !!item.isResellerCloudBranded;
        /**
         * @type {?vpsInfo}
         */
        this.info = null
        if(item.oid) {
            this.oid = item.oid
        }
        this.dcLocation = item.location;
        this.platform = VPSPlatform.inst
        /** @type {?GET.Services.vps.id.reverseDNS.output} */
        this.reverseDNS = null
        /** @type {?GET.Services.vps.id.stats.output} */
        this.stats = null
        this.store = store
        /** @type {?GET.Services.vps.id.rebuildOperatingSystems.output} */
        this.suggestedOperatingSystemCodes = null
        /** @type {?VNC} */
        this.vnc = null
        this._addresses = null
        this.ownedStackUser = item.ownedStackUser
        this.profileIdCache = item.profileIdCache
        this.packages = item.packages ?? []
        this.packageIds = item.packageIds ?? []
        this.expiryDate = item.expiryDate?.date ?? null
        this.renewalDate = item.renewalDate?.date ?? null
        this.preferredRenewalAction = item.preferredRenewalAction ?? null
    }

    /** @type {VPSIPAddressInfo[]|null|undefined} */
    get addresses() {
        if(!this.info) return null
        if(!this.info.Network) return undefined
        //if(!this.reverseDNS) return null
        if(!this._addresses) {
            this._addresses = this.info.Network[0].Addresses.map(
                a => new VPSIPAddress(
                    this,
                    {
                        ip: a.IpAddress,
                        subnetMask: a.IPAddressRange.Netmask,
                        defaultGateway: a.IPAddressRange.GatewayIpAddress,
                        version: a.IpVersion
                    },
                    //this.reverseDNS[a.IpAddress]
                    null
                )
            )
        }
        return this._addresses
    }

    /** @type {?string} eg. "WordPress 7.5.2 with our extensions" */
    get appInternalLabel() {
        if(!this.info) return null
        if(this.info.OS) {
            return(
                this.info.Application &&
                this.info.Application.DisplayName ||
                null
            )
        } else {
            const config = this.info && this.info.configuration
            const app = config && VPSPlatform.inst.apps &&
            VPSPlatform.inst.apps.find(
                a => a.id == config.ApplicationId
            )
            return app && app.name || null
        }
    }

    get backupCopies() {
        if(!this.backups) return null
        /** @type {VPSBackupCopy[]} */
        const out = []
        for(const b of this.backups) {
            out.push(...b.copies)
        }
        return out
    }

    get canUseCpanel() {
        return this.info && this.info.cpanel
    }

    get configuredInfo() {
        return this.info && new VPSConfiguredInfo(this.info.configuration, this.info.productOptions)
    }

    /** @type {?string} eg. "10.0.0.1:2083" */
    get cPanelURL() {
        return(
            this.osInternalLabel &&
            this.osInternalLabel.match(/cPanel/) &&
            `http://${this.addresses[0].ip}/whm`
        )
    }

    get cpanelUrlLicense() {
        if(this.platformInfo && this.platformInfo.info.AddOnId && this.addresses) {
            return `http://${this.addresses[0].ip}/whm`
        }
    }

    get cpanelLicenseId() {
        if(this.platformInfo && this.platformInfo.info) {
            return this.platformInfo.info.AddOnId
        }
    }

    get cpanelInfo() {
        if(this.info && this.info.AddOns) {
            let c = this.info.AddOns
                .find(a => a.AddOnId === this.cpanelLicenseId)
            if(c) {
                return {
                    capacity: c.Description,
                    license: c.Name.replace('cPanel ', ''),
                }
            } else {
                return null
            }
        }
    }

    /** @type {?number} eg. 2 for 2 cores */
    get cpu() {
        return this.info && this.info.CpuCores || null
    }

    /** @type {?Date} */
    get created() {
        return this.info && this.info.CreatedAt && new Date(this.info.CreatedAt) || null
    }

    get currentOperatingSystemOption() {
        var cacheos =  this.info && this.info.productOptions && this.info.productOptions.os;
        return this.product && this.product.operatingSystems.find(
            os => os.code == cacheos
        )
    }

    /** @type {?string} */
    get currentPlatformAction() {
        return this.info && this.info.Status && this.info.Status.CurrentAction || null
    }

    /** @type {?number} eg. 20 for 20GB */
    get disk() {
        return this.info?.configuration?.OsDiskSizeGb;
    }

    /** @type {?boolean} */
    get hasIpv6() {
        return !!this.addresses.find( i => i.version == 6 );
    }

    /** @type {?string} */
    get hostname() {return this.info && this.info.name}

    /**
     * @type {?VPSApplication}
     */
    get installedApp() {
        return(
            this.info &&
            this.info.Application &&
            new VPSApplication(this.info.Application) ||
            null
        )
    }

    /** @type {?string} */
    get ipv4() {
        var ip = this.info?.Network && this.info.Network[0]?.Addresses?.find( i => i.IpVersion == 4 );
        return ip ? ip.IpAddress : null;
    }

    /** @type {?string} */
    get ipv6() {
        var ip = this.info?.Network[0]?.Addresses?.find( i => i.IpVersion == 6 );
        return ip ? ip.IpAddress : null;
    }

    /** @type {?boolean} */
    get isRebooting() {
        return this.info && this.info.isRebooting || null
    }
    get lastBackupCopy() {
        if(!this.backupCopies) return null
        return this.backupCopies[
            this.backupCopies.length - 1
        ]
    }
    /**
     * @type {?string} eg. "/admin"
     */
    get managementPath() {
        if(!(this.info && this.info.Application)) return null
        if(this.info.Application.AdminUrl) {
            return this.info.Application.AdminUrl
        }
        switch(this.info.Application.ApplicationId) {
            case 4: // CMS Made Simple
                return "/cmsms/admin"
            case 5: // Concrete5
                return "/index.php/login"
            case 6: // CraftCMS
                return "/admin"
            case 7: // Drupal 8
                return "/admin"
            case 8: // Elgg
                return "/elgg"
            case 10: // Joomla
                return "/administrator"
            case 13: // Moodle
                return "/admin/index.php"
            case 14: // OpenCart
                return "/admin"
            case 16: // Prestashop
                return "/admin"
            case 20: // Wordpress
                return "/wp-admin"
            case 21: // ZenCart
                return "/storename/admin"
            default:
                return "/"
        }
    }

    /**
     * @type {?string} eg. http://10.0.0.0/admin
     */
    get managementURL() {
        if(this.managementPath && this.managementPath != "/" && this.addresses) {
            return `http://${this.addresses[0].ip}${this.managementPath}`
        } else {
            return null
        }
    }

    /** @type {?string} */
    get name() {
        return this.info && this.info.name
    }

    /** @type {?string} */
    get kvmHostname() {
        return(
            this.info &&
            this.info.KVMServer &&
            this.info.KVMServer.split(".")[0] ||
            null
        )
    }

    get networkPropertyHandlers() {
        return {
            disks: () =>  $.ajax(`/a/`+(this.isManaged?'managed_vps':'vps')+`/${this.id}/disk`).done(
                /** @param {GET.Services.vps.id.disks.output & {oid: string}} result */
                result => {
                }
            ),
            info: () => $.ajax(`/o/`+(this.isManaged?'managed_vps':'vps')+`/${this.id}`).done(
                /** @param {GET.Services.vps.id.output & {oid: string}} result */
                result => {
                    if( result.location ){
                        this.dcLocation = result.location
                    }
                    if(result.isRebooting) {
                        setTimeout(() => {
                            this.reload(["info"])
                        }, 5000)
                    }
                }
            ),
            backups: () => $.ajax(`/a/`+(this.isManaged?'managed_vps':'vps')+`/${this.id}/backups`).then(
                /** @param {GET.Services.vps.id.backups.output} result */
                result => $.Deferred().resolve(result.map(
                    r => new VPSBackup(this, r)
                ))
            ),
            reverseDNS: () => $.ajax(`/a/`+(this.isManaged?'managed_vps':'vps')+`/${this.id}/reverseDNS`),
            stats: () => $.ajax(`/a/`+(this.isManaged?'managed_vps':'vps')+`/${this.id}/stats`),
            suggestedOperatingSystemCodes: () => $.ajax(
                `/a/vps/${this.id}/rebuildOperatingSystems`
            ),
            vnc: () => $.ajax(`/a/`+(this.isManaged?'managed_vps':'vps')+`/${this.id}/vnc`).then(
                /** @param {GET.Services.vps.id.vnc.output} result */
                result => $.Deferred().resolve(new VNC(this.id, result))
            )
        }
    }

    /** @type {?string} */
    get oid() {
        if(this._oid) {
            return this._oid
        } else {
            return this.info && this.info.oid
        }
    }
    set oid(v) {
        this._oid = v
    }

    /** @type {?string} eg. "rhel6" */
    get os() {
        return this.info && this.info.OS && this.info.OS.LibvirtOsVariant || null
    }

    /** @type {?string} eg. "CentOS 6.6 (x86_64, rebuild, console disabled)" */
    get osInternalLabel() {
        if( this.isManaged ){
            if(this.info?.OS) {
                return this.info.OS.DisplayName;
            } else {
                return translation.get('vps_service_overview_shared_managed_centos_7');
            }
        } else {
            if(!this.info) return null
            if (this.info.OS) {
                return this.info.OS.DisplayName
            } else {
                return this.configuredInfo && this.configuredInfo.operatingSystemLabel
            }
        }
    }

    /** Explicitly called by the reveal action; never an observed/lazy property. */
    fetchRootPassword() {
        if (this.isManaged) return Promise.resolve(null);
        return $.ajax({url: `/a/vps/${this.id}/rootPassword`, cache: false}).then(result => result.password);
    }

    /** Changing the password does not read the existing password. */
    set password(v) {
        this.changePassword(v);
    }

    changePassword(v) {
        return $.ajax({
            method: "post",
            url: `/a/vps/${this.id}/changePassword`,
            data: {password: v},
        }).done(() => {
            ShowNotification.success(
                translation.get('vps_service_overview_shared_notification_success_updated_password')
            );
            this.reload(["info"]);
        }).fail(() => {
            ShowNotification.fail(
                translation.get('vps_service_overview_shared_notification_fail_updating_password')
            );
        });
    }

    /** @type {?string} The VPS product name */
    get plan() {
        return this.product && this.product.name
    }

    /** @type {?boolean} */
    get platformCanChangePassword() {
        if(!this.info) return null
        if(!this.info.OS) return null
        switch(this.info.OS.LibvirtOsType) {
            case "linux":
                return true
            case "windows":
                return false
            default:
                return null
        }
    }

    get platformInfo() {
        return this.info && this.info.OS && new VPSPlatformInfo(this.info)
    }

    /** @type {?VPSProduct} */
    get product() {
        if( this.isManaged ){
          return(
                this.store &&
                this.store.managedVpsProducts &&
                this.productSpec &&
                this.store.managedVpsProducts.find(
                    p => p.productCode == this.productSpec
                ) ||
                null
            )
        } else {
          return(
                this.store &&
                this.store.vpsProducts &&
                this.productSpec &&
                this.store.vpsProducts.find(
                    p => p.productCode == this.productSpec
                ) ||
                null
            )
        }
      }

    /** @type {?{[x: string]: string}} */
    get productOptions() {
        return this.info && this.info.productOptions || null
    }

    /** @type {?string} */
    get productSpec() {
        return this.info && this.info.productSpec
    }

    /** @type {?number} eg. 2 for 2GB */
    get ram() {
        return this.info && this.info.RamMb && Math.round(this.info.RamMb / 1024) || null
    }

    get rebuildInProgress() {
        return this.status == "build"
    }

    /** @type {?VPSOperatingSystemOptionGroup[]} */
    get rebuildOperatingSystemsGrouped() {
        if(!this.product) return null
        const current_os_option = this.currentOperatingSystemOption
        if(!current_os_option) return null
        return VPSOperatingSystemOptionGroup.from(
            this.product.operatingSystems.filter(
                os => os.mayRebuildFrom(current_os_option)
            )
        )
    }

    get serviceType() {
        if ( this.isManaged ) {
            return "managed_vps";
        } else {
            return "vps"
        }
    }

    /**
     * @type {?{cpu: [Date, number][], cpuPercent: [Date, number][], cpuPercentComputed: [Date, number][], disk: [Date, number][], traffic: [Date, number][]}}
     */
    get statsByType() {
        if(!this.stats) return null
        const by_type = {
            /** @type {[Date, number][]} */
            cpu: [],
            /** @type {[Date, number][]} */
            cpuPercent: [],
            /** @type {[Date, number][]} */
            cpuPercentComputed: [],
            /** @type {[Date, number][]} */
            disk: [],
            /** @type {[Date, number][]} */
            traffic: [],
        }
        let last_row
        this.stats.forEach(snapshot => {
            let created_at = new Date(snapshot.CreatedAt)
            by_type.disk.push(
                [
                    created_at,
                    parseFloat(snapshot.OsDiskReadBytes) +
                    parseFloat(snapshot.OsDiskWriteBytes)
                ]
            )
            by_type.cpu.push(
                [
                    created_at,
                    snapshot.AllCpuUser
                ]
            )
            by_type.cpuPercent.push(
                [
                    created_at,
                    Math.max(
                        0,
                        Math.min(snapshot.CalculatedUtilisationPercent, 100)
                    )
                ]
            )
            if(this.cpu && last_row) {
                let time_diff = created_at.valueOf() -
                    new Date(last_row.CreatedAt).valueOf()
                by_type.cpuPercentComputed.push(
                    [
                        created_at,
                        (
                            parseFloat(snapshot.AllCpuUser) +
                            parseFloat(snapshot.AllCpuSystem)
                        ) * 100 * 1000 / (1000000000 * this.cpu * time_diff)
                    ]
                )
            }
            by_type.traffic.push(
                [
                    created_at,
                    parseFloat(snapshot.Eth0RxBytes) +
                    parseFloat(snapshot.Eth0TxBytes)
                ]
            )
            last_row = snapshot
        })
        return by_type
    }

    /**
     * @type {"restarting" | "wait" | "active" | "offline" | "disabled" | "build" | "unknown" | null}
     */
    get status() {
        if(!this.info) return null
        if(!this.info.Status) return "unknown"
        if ('BuildComplete' in this.info) {
            if (!this.info.BuildComplete) {
                return "build"
            }
        }
        if(
            this.info.Status.CurrentAction == "build" ||
            this.info.Status.CurrentAction == "rebuild"
        ) {
            return "build"
        }
        if(
            this.info.Status.CurrentAction == "hard_reboot" ||
            this.info.Status.CurrentAction == "reboot"
        ) {
            return "restarting"
        }
        if(this.info.Status.PendingAction) {
            return "wait"
        }
        switch(this.info.Status.Domstate) {
            case "running":
                return "active"
            case "defined":
            case "stopped":
            case "shut off": // sic
                return "offline"
            case "paused":
            case "saved":
            case "suspended": // sic
                return "disabled"
            default:
                return null
        }
    }

    get suggestedOperatingSystems() {
        if(!this.platform.operatingSystems) return null
        if(!this.suggestedOperatingSystemCodes) return null
        /**
         * @type {VPSSuggestedOperatingSystem[]}
         */
        const out = []
        for(const c of this.suggestedOperatingSystemCodes) {
            const o = this.platform.operatingSystems.find(
                os => os.osVariant == c
            )
            if(o) {
                out.push(new VPSSuggestedOperatingSystem(c, o))
            }
        }
    }

    /** @type {?VPSSuggestedOperatingSystemGroup[]} */
    get suggestedOperatingSystemsGrouped() {
        return(
            this.suggestedOperatingSystems &&
            VPSSuggestedOperatingSystemGroup.from(
                this.suggestedOperatingSystems
            ) ||
            null
        )
    }

    /** @type {?string} eg. "root" */
    get username() {
        if(!this.info) return null
        if(!this.info.OS) return null
        switch(this.info.OS.LibvirtOsType) {
            case "linux":
                return "root"
            case "windows":
                return "Administrator"
            default:
                return null
        }
    }

    /** @type {?string} eg. "1.1.1.1:2" */
    get vncDisplayAddress() {
        return this.vnc && this.vnc.connectionAddress || null
    }

    /**
     * @type {?string} eg. http://10.0.0.0/wordpress
     */
    get websiteURL() {
        return this.addresses && `http://${this.addresses[0].ip}` || null
    }

    /**
     * Add a free IPV6 address
     * @return {JQuery.Promise}
     */
    addIpv6Address(){
        return $.ajax({
            method: "post",
            url: `/a/`+(this.isManaged?'managed_vps':'vps')+`/${this.id}/ipv6Address`,
            data: {
                "ipVersion": 6
            }
        }).done((r) => {
            if( r.result.error ){
                ShowNotification.fail(
                    translation.get('vps_service_overview_shared_notification_fail_adding_ipv6')
                )
            } else {
                ShowNotification.success(
                    translation.get('vps_service_overview_shared_notification_success_added_ipv6')
                )
                this.info = null;
                this._addresses = null;
                this.reload(["info"])
            }
        }).fail(() => {
            ShowNotification.fail(
                translation.get('vps_service_overview_shared_notification_fail_adding_ipv6')
            )
        });
    }

    /**
     * Reboots.
     *
     * @returns {JQuery.Promise}
     */
    reboot() {
        return $.ajax({
            method: "post",
            url: `/a/`+(this.isManaged?'managed_vps':'vps')+`/${this.id}/reboot`,
            data: {
                "Action": "reboot",
                "State": "new"
            }
        }).done(() => {
            ShowNotification.success(
                translation.get('vps_service_overview_shared_notification_sucess_reboot')
            )
            this.reload(["info"])
        }).fail(() => {
            ShowNotification.fail(
                translation.get('vps_service_overview_shared_notification_fail_reboot')
            )
        });
    }

    /**
     * Rebuilds the server with either an OS spec or an app spec
     *
     * @param {?Object} [os]
     * @param {?VPSApplication} [app]
     * @param {?string} with_cpanel
     * @returns {?JQuery.jqXHR}
     */
    rebuildOperatingSystem(os = null, app = null, with_cpanel = null) {
        let args = {}
        if(!(os || app || with_cpanel)) {
            console.log("No OS or app")
            ShowNotification.fail(
                translation.get('vps_service_overview_shared_notification_fail_starting_rebuild')
            )
            return null
        }
        if(os) {
            args.VpsOsId = os.id
            args.osVariant = os.osVariant
        }
        if(app) {
            args.ApplicationId = app.id
        }
        if(with_cpanel) {
            args.cpanel = 1;
            args.cpanelCode = with_cpanel;
        }
        if(this.info && this.info.Status) {
            this.info.Status.CurrentAction = "rebuild"
        }
        return $.ajax({
            method: "post",
            url: `/a/`+(this.isManaged?'managed_vps':'vps')+`/${this.id}/rebuild`,
            data: args,
        }).done(() => {
            ShowNotification.success(
                translation.get('vps_service_overview_shared_notification_sucess_server_rebuild')
            );
            window.location.href = `/services/${(this.isManaged ? 'managed_vps' : 'vps')}/${this.oid ? this.oid : this.id}/service-overview`
        }).fail(() => {
            ShowNotification.fail(
                translation.get('vps_service_overview_shared_notification_fail_server_rebuild')
            );
        });
    }

    /**
     * Set a custom name for this VPS
     * @return {JQuery.Promise}
     */
    setVpsName(name){
        return $.ajax({
            method: "post",
            url: `/a/`+(this.isManaged?'managed_vps':'vps')+`/${this.id}/name`,
            data: {
                "name": name
            }
        }).done(() => {
            ShowNotification.success(
                translation.get('vps_service_overview_shared_notification_success_set_name')
            )
            this.reload(["info"])
        }).fail(() => {
            ShowNotification.fail(
                translation.get('vps_service_overview_shared_notification_fail_set_name')
            )
        });
    }

    /**
     * Starts the server up (turns on).
     *
     * @returns {JQuery.Promise}
     */
    start() {
        return $.ajax({
            method: "post",
            url: `/a/`+(this.isManaged?'managed_vps':'vps')+`/${this.id}/start`,
            data: {
                "Action": "start",
                "State": "new"
            }
        }).done(() => {
            ShowNotification.success(
                translation.get('vps_service_overview_shared_notification_success_booting')
            )
            this.reload(["info"])
        }).fail(() => {
            ShowNotification.fail(
                translation.get('vps_service_overview_shared_notification_fail_booting')
            )
        });
    }

    /**
     * Shuts the server down (turns off).
     *
     * @returns {JQuery.Promise}
     */
    stop() {
        return $.ajax({
            method: "post",
            url: `/a/`+(this.isManaged?'managed_vps':'vps')+`/${this.id}/stop`,
            data: {
                "Action": "shutdown",
                "State": "new"
            }
        }).done(() => {
            ShowNotification.success(
                translation.get('vps_service_overview_shared_notification_success_turning_off')
            )
            this.reload(["info"])
        }).fail(() => {
            ShowNotification.fail(
                translation.get('vps_service_overview_shared_notification_fail_turning_off')
            )
        });
    }
}
